import { getPageOrder } from '../webDataPageOrder';
import { pageBlockIds } from '../../comments/commentAnchor';

/**
 * Les deux éditeurs, ramenés à une même liste de « portées ».
 *
 * Le Web Editor tient ses pages dans `webData.pages` et son squelette dans
 * `webData.skeleton` ; l'éditeur de modèle tient les siennes dans
 * `static_pages` et quatre colonnes `*_puck_data`. C'est la SEULE différence
 * qui compte pour le détecteur de liens cassés : normalisée ici, elle
 * n'apparaît nulle part ailleurs.
 *
 * Les identifiants de portée sont ceux que la collaboration, les commentaires
 * et la sauvegarde automatique emploient déjà (`page:<slug>`, `header`…) : un
 * problème peut donc être repassé tel quel à la navigation de l'éditeur, sans
 * table de traduction.
 *
 * @typedef {{ id: string, kind: string, label: string, pageSlug: string,
 *             puckData: object }} SiteScope
 */

/** Sections du squelette, dans l'ordre où l'éditeur les présente. */
const SKELETON = [
  { kind: 'header', label: 'En-tête', webKey: 'header', templateKey: 'header_puck_data' },
  { kind: 'footer', label: 'Pied de page', webKey: 'footer', templateKey: 'footer_puck_data' },
  { kind: 'login', label: 'Page de connexion', webKey: 'login', templateKey: 'login_page_puck_data' },
  { kind: 'loading', label: 'Page de chargement', webKey: 'loading', templateKey: 'loading_page_puck_data' },
];

const PAGE_SCOPE_PREFIX = 'page:';

/**
 * Où vivent les données Puck d'une portée, dans le `webData` d'un site ou dans
 * l'instantané d'un modèle : le chemin de clés jusqu'à `{ content, zones }`.
 */
export function scopeDataPath(scopeId, { template = false } = {}) {
  if (scopeId.startsWith(PAGE_SCOPE_PREFIX)) {
    const slug = scopeId.slice(PAGE_SCOPE_PREFIX.length);
    return [template ? 'static_pages' : 'pages', slug, 'puckData'];
  }
  const section = SKELETON.find(({ kind }) => kind === scopeId);
  if (!section) return null;
  return template ? [section.templateKey] : ['skeleton', section.webKey];
}

const pageScope = (slug, page) => ({
  id: `${PAGE_SCOPE_PREFIX}${slug}`,
  kind: 'page',
  label: page?.title || slug,
  pageSlug: slug,
  puckData: page?.puckData || null,
});

const hasContent = (scope) => !!scope.puckData;

/** `webData` du document → portées. */
export function webEditorScopes(webData) {
  const pages = webData?.pages || {};
  const scopes = getPageOrder(webData)
    .filter((slug) => pages[slug])
    .map((slug) => pageScope(slug, pages[slug]));

  SKELETON.forEach(({ kind, label, webKey }) => {
    const puckData = webData?.skeleton?.[webKey];
    if (puckData) {
      scopes.push({
        id: kind, kind, label, pageSlug: '', puckData,
      });
    }
  });
  return scopes.filter(hasContent);
}

/** Instantané d'un modèle de site → portées. */
export function webTemplateScopes(snapshot) {
  const pages = snapshot?.static_pages || {};
  const order = Array.isArray(snapshot?.static_page_order)
    ? snapshot.static_page_order.filter((slug) => pages[slug])
    : [];
  const rest = Object.keys(pages).filter((slug) => !order.includes(slug));
  const scopes = [...order, ...rest].map((slug) => pageScope(slug, pages[slug]));

  SKELETON.forEach(({ kind, label, templateKey }) => {
    const puckData = snapshot?.[templateKey];
    if (puckData) {
      scopes.push({
        id: kind, kind, label, pageSlug: '', puckData,
      });
    }
  });
  return scopes.filter(hasContent);
}

/** Les ancres lisibles posées sur les blocs racine d'une page. */
function pageAnchors(puckData) {
  return (puckData?.content || [])
    .map((block) => block?.props?.anchor)
    .filter(Boolean);
}

/**
 * Ce qu'un lien interne peut viser : les slugs de page, et les blocs — par leur
 * id **ou** par leur ancre, les deux étant acceptés dans un `href="#…"`.
 *
 * Les ids passent par `pageBlockIds`, seul parcours du projet qui couvre déjà
 * `content` **et** `zones` : un bouton posé dans une colonne reste une cible
 * valide, et le manquer ferait signaler des liens parfaitement vivants.
 */
export function scopesTargets(scopes) {
  const pages = new Set();
  const blockIds = new Set();
  (scopes || []).forEach((scope) => {
    if (scope.pageSlug) pages.add(scope.pageSlug);
    pageBlockIds(scope.puckData).forEach((id) => blockIds.add(id));
    pageAnchors(scope.puckData).forEach((anchor) => blockIds.add(anchor));
  });
  return { pages, blockIds };
}
